import {Directive, ElementRef, EventEmitter, HostListener, Input, Output, SimpleChanges} from '@angular/core';

@Directive({
    selector: '[appContentEditableModel]',
    host: {
        '(blur)': 'save()'
    },
    standalone: false
})
export class ContentEditableModelDirective {
  @Input('contenteditableModel') model: string;
  @Input('maxCharacters') maxChars: number = 0;
  @Input('hasLimit') hasLimit: boolean = false;
  @Output('contenteditableModelChange') focusOut: EventEmitter<string> = new EventEmitter<string>();

  /**
   * By updating this property on keyup, and checking against it during
   * ngOnChanges, we can rule out change events fired by our own onKeyup.
   * Ideally we would not have to check against the whole string on every
   * change, could possibly store a flag during onKeyup and test against that
   * flag in ngOnChanges, but implementation details of Angular change detection
   * cycle might make this not work in some edge cases?
   */
  private lastViewModel: string;

  constructor(private elRef: ElementRef) {
  }

  @HostListener('paste', ['$event'])
  pasteAsPlainText(event){
    let clipboardData, pastedData;

    // Stop data actually being pasted into div
    event.stopPropagation();
    event.preventDefault();

    // Get pasted data via clipboard API
    clipboardData = event.clipboardData || window[ 'clipboardData' ];
    pastedData = clipboardData.getData( 'Text' );

    this.insertTextAtCaret( pastedData );
    this.updateValue();
    //and now trim it
    let charsLeft = this.maxChars - this.lastViewModel.length;
    if( this.hasLimit && (charsLeft < 0 ) ) {
      pastedData = this.lastViewModel.substring(0, this.maxChars);
      this.elRef.nativeElement.innerText = pastedData;
      this.updateValue();
    }
  }

  insertTextAtCaret(text) {
    // use insertText command if supported
    if (document.queryCommandSupported('insertText')) {
      document.execCommand('insertText', false, text);
    }
    // or insert the text content at the caret's current position
    // replacing eventually selected content
    else {
      let range = document.getSelection().getRangeAt(0);
      range.deleteContents();
      let textNode = document.createTextNode(text);
      range.insertNode(textNode);
      range.selectNodeContents(textNode);
      range.collapse(false);

      let selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['model'] && changes['model'].currentValue !== this.lastViewModel) {
      this.lastViewModel = this.model;
      this.refreshView();
    }
  }

  /** This should probably be debounced. */
  save() {
    this.focusOut.emit(this.updateValue());
  }

  private updateValue() {
    let value = this.elRef.nativeElement.innerText;
    this.lastViewModel = value;
    return value;
  }

  private refreshView() {
    this.elRef.nativeElement.innerText = this.model;
  }
}
